import { createFileRoute } from "@tanstack/react-router";
import { CreditCard, Check, Phone, ShieldCheck, Clock, Calculator, Wallet } from "lucide-react";
import { openQuoteModal } from "../components/quote-modal";
import { Reveal } from "../components/reveal";
import { Breadcrumbs } from "../components/breadcrumbs";
import {
  FINANCING_MIN,
  FINANCING_MAX,
  PREQUALIFY_URL,
  PROMO_HEADLINE,
  PAYMENT_METHODS,
} from "../data/financing";
import { InlineQuoteSection } from "@/components/inline-quote-section";

const HIGHLIGHTS = [
  {
    icon: Calculator,
    title: `${PROMO_HEADLINE}`,
    desc: "Qualified customers can take advantage of a 6-month no-interest promotional term — pay it off inside the promo window and you pay zero interest.",
  },
  {
    icon: CreditCard,
    title: `Projects From $${FINANCING_MIN}`,
    desc: `Financing is available on any project starting at $${FINANCING_MIN} and up to $${FINANCING_MAX.toLocaleString()}, so it works for a single house wash or a full exterior restoration.`,
  },
  {
    icon: ShieldCheck,
    title: "No Credit Score Impact To Check",
    desc: "Checking your options is a soft pull. See what you qualify for without touching your credit score.",
  },
  {
    icon: Clock,
    title: "About A Minute To Apply",
    desc: "Quick online application on your phone. No paperwork, no hidden fees, and no compounding interest.",
  },
];

const BULLETS = [
  `Finance projects from $${FINANCING_MIN} up to $${FINANCING_MAX.toLocaleString()}*`,
  "Terms from 3 to 60 months*",
  "APRs range from 0 to 29.99%*",
  "No hidden fees and no compounding interest",
  "6 months no interest on qualifying promotional plans*",
];

const STEPS = [
  {
    title: "Get Your Free Quote",
    desc: "Send us photos or book a quick call. We price your project and send a written quote — no obligation.",
  },
  {
    title: "Check Your Options",
    desc: "Apply online in about a minute. Checking your options is a soft credit pull and won't affect your score.",
  },
  {
    title: "Pick Your Plan",
    desc: "Choose the monthly payment and term that fits your budget, including the 6-month no-interest option if you qualify.",
  },
  {
    title: "We Get To Work",
    desc: "Once you're approved and scheduled, we clean. You pay over time instead of all at once.",
  },
];

const FAQS = [
  {
    q: "What is the minimum project size for financing?",
    a: `Financing is available on projects starting at $${FINANCING_MIN}. Smaller jobs can still be paid by card, check, or cash.`,
  },
  {
    q: "Does checking my financing options hurt my credit?",
    a: "No. Checking your options is a soft credit check and does not impact your credit score. A hard pull only happens if you choose to move forward with a loan.",
  },
  {
    q: "How does the 6 months no interest work?",
    a: "Qualifying customers can select a 6-month promotional plan. If the balance is paid in full within the promo period, you pay no interest. All financing is subject to credit approval and terms may vary.",
  },
  {
    q: "Can I pay my project off early?",
    a: "Yes. There are no prepayment penalties — pay it off any time without extra fees.",
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept all major credit and debit cards, Venmo, Zelle, PayPal, Klarna, plus cash and check.",
  },
  {
    q: "Which services can I finance?",
    a: "Any of our services qualify, including house washing, roof washing, paver cleaning and sealing, concrete cleaning and sealing, gutter guards, and permanent outdoor lighting.",
  },
];

export const Route = createFileRoute("/financing")({
  head: () => ({
    meta: [
      { title: "Financing | 6 Months No Interest on $500+ Projects" },
      {
        name: "description",
        content:
          "Finance your exterior cleaning project in the Capital Region. 6 months no interest for qualified buyers, projects from $500, no credit impact to check.",
      },
      { property: "og:title", content: "Financing — 6 Months No Interest" },
      {
        property: "og:description",
        content:
          "Pay over time on projects starting at $500. 6 months no interest for qualified customers. Checking your options takes a minute with no credit impact.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://capitalpropressurewashing.com/financing" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://capitalpropressurewashing.com/financing" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: FinancingPage,
});

function FinancingPage() {
  return (
    <main>
      <Breadcrumbs items={[{ label: "Financing" }]} />
      {/* Hero */}
      <section className="bg-[var(--brand-deep-blue)] text-white py-14 md:py-20">
        <div className="mx-auto max-w-[1200px] px-4 text-center">
          <p
            className="text-[#ff6b7f] font-semibold uppercase tracking-[0.2em] text-sm mb-3"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Financing Available
          </p>
          <h1
            className="text-3xl md:text-5xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            {PROMO_HEADLINE} On Your Next Project
          </h1>
          <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            We'd love to make your next project easier to complete. Pay over time instead of
            all at once on any project starting at ${FINANCING_MIN}. The application takes about a
            minute and checking your options does not impact your credit score.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={PREQUALIFY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-md bg-[#C8102E] text-white px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Prequalify Now
            </a>
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="inline-flex items-center justify-center rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Get Free Quote
            </button>
          </div>
          <a
            href="tel:+15189001913"
            className="mt-5 inline-flex items-center gap-2 text-white/85 hover:text-white text-sm"
          >
            <Phone size={16} strokeWidth={2.5} />
            Questions? Call (518) 900-1913
          </a>
        </div>
      </section>

      {/* Highlights */}
      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[1200px] px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {HIGHLIGHTS.map((h, i) => {
              const Icon = h.icon;
              return (
                <Reveal
                  key={h.title}
                  delayMs={i * 70}
                  className="card-pop bg-[var(--brand-light-gray)] rounded-lg p-6 border border-gray-200"
                >
                  <div className="w-11 h-11 rounded-md bg-[var(--brand-red)]/10 text-[var(--brand-red)] flex items-center justify-center mb-4">
                    <Icon size={22} />
                  </div>
                  <h2
                    className="text-[var(--brand-deep-blue)] text-lg uppercase mb-2 leading-tight"
                    style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                  >
                    {h.title}
                  </h2>
                  <p className="text-[var(--brand-charcoal)]/85 text-sm leading-relaxed">
                    {h.desc}
                  </p>
                </Reveal>
              );
            })}
          </div>

          <Reveal className="mt-8 rounded-lg bg-[var(--brand-deep-blue)] text-white p-6 md:p-8">
            <h2
              className="text-xl md:text-2xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              What You Get
            </h2>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-2 text-white/90 text-sm leading-relaxed">
                  <Check size={16} strokeWidth={3} className="text-[#C8102E] mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[var(--brand-light-gray)] py-14 md:py-16">
        <div className="mx-auto max-w-[1200px] px-4">
          <div className="text-center mb-8 md:mb-10">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              How It Works
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Four Simple Steps
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <Reveal
                key={s.title}
                delayMs={i * 80}
                className="relative bg-white rounded-lg p-6 border border-gray-200"
              >
                <div
                  className="absolute -top-4 -left-2 bg-[var(--brand-deep-blue)] text-white w-12 h-12 rounded-full flex items-center justify-center text-xl shadow-md"
                  style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                >
                  {i + 1}
                </div>
                <h3
                  className="mt-3 text-[var(--brand-deep-blue)] text-lg uppercase mb-2 leading-tight"
                  style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                >
                  {s.title}
                </h3>
                <p className="text-[var(--brand-charcoal)]/85 text-sm leading-relaxed">{s.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[1200px] px-4 mb-14 md:mb-16">
          <Reveal className="rounded-lg border border-gray-200 bg-[var(--brand-light-gray)] p-6 md:p-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 rounded-md bg-[var(--brand-red)]/10 text-[var(--brand-red)] flex items-center justify-center shrink-0">
                <Wallet size={22} />
              </div>
              <h2
                className="text-[var(--brand-deep-blue)] text-xl md:text-2xl uppercase leading-tight"
                style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
              >
                Payment Options We Accept
              </h2>
            </div>
            <p className="text-[var(--brand-charcoal)]/85 text-sm md:text-base leading-relaxed">
              Not financing? No problem. Pay however is easiest for you.
            </p>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
              {PAYMENT_METHODS.map((p) => (
                <li
                  key={p}
                  className="flex items-start gap-2 text-[var(--brand-charcoal)]/90 text-sm md:text-base leading-relaxed"
                >
                  <Check
                    size={16}
                    strokeWidth={3}
                    className="text-[var(--brand-red)] mt-1 shrink-0"
                  />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <div className="mx-auto max-w-[900px] px-4">
          <h2
            className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight text-center mb-8"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Financing Questions
          </h2>
          <div className="space-y-4">
            {FAQS.map((f) => (
              <details
                key={f.q}
                className="group bg-[var(--brand-light-gray)] rounded-lg border border-gray-200 p-5"
              >
                <summary
                  className="cursor-pointer list-none text-[var(--brand-deep-blue)] text-base md:text-lg uppercase leading-tight"
                  style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                >
                  {f.q}
                </summary>
                <p className="mt-3 text-[var(--brand-charcoal)]/85 text-sm md:text-base leading-relaxed">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + disclosure */}
      <section className="bg-[var(--brand-deep-blue)] text-white py-12">
        <div className="mx-auto max-w-[1200px] px-4 text-center">
          <h2
            className="text-2xl md:text-3xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Ready To Get Started?
          </h2>
          <p className="mt-2 text-white/85">
            Get your free quote first, then choose the payment plan that fits.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="inline-flex items-center justify-center rounded-md bg-[#C8102E] text-white px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Get Free Quote
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              <Phone size={18} strokeWidth={2.5} />
              Call (518) 900-1913
            </a>
          </div>
          <p className="mt-8 text-white/60 text-xs leading-relaxed max-w-3xl mx-auto">
            *All financing is subject to credit approval. Terms may vary and promotional
            no-interest plans are available to qualified applicants only. Payment options are
            provided by our third-party lending partners; Capital Pro Pressure Washing is not a
            lender and does not make credit decisions.
          </p>
        </div>
      </section>
      <InlineQuoteSection source="Financing" />
    </main>
  );
}
